// External
import { useCallback, useMemo, useState } from "react";
import { useQuery, useReadQuery } from "@apollo/client";
import _ from "lodash";
import { useTranslation } from "react-i18next";

// Internal
import { useDispatch } from "Hooks/useDispatch";
import { getOrganizationId } from "modules/CommonSelectors";
import { setToastMsg } from "Login/modules/LoginModule";
import { useAcademicRecordsModal } from "../../../../../context";
import { getOrgTranscriptEntitiesQuery } from "../../../../../module/AcademicRecordsQueries";
import {
  updateTranscriptConfigurationHandler,
  bulkCopyTranscriptConfigurationHandler,
  updateStudentGradeGradingPeriodMappingHandler,
  bulkUpdateStudentGradeGradingPeriodMappingHandler,
  updateTranscriptGradingPeriodsHandler,
} from "../../../../../module/AcademicRecordsThunks";

// Relative
import config from "../../../modules/config.json";
import {
  SECTION_NODE_KEY,
  GRADE_WISE_NODE_KEY,
  DEFAULT_COLUMNS_ROWS_KEY,
  ACROSS_YEAR_AGGREGATES_KEY,
  GRADE_OPTION_KEY_PREFIX,
  GRADING_PERIODS_SETTINGS_KEY,
} from "../../../modules/constants";
import {
  getConfigurationPayload,
  getBulkConfigurationPayload,
  getDraftConfiguration,
  getGradeWiseOptions,
  getInitialSelectedTab,
  getResetDraftConfiguration,
  getSelectableKeys,
  getSelectedDraftKey,
  getSelectedGradeId,
  mapDraftPeriodsToBackendShape,
} from "../modules/displaySettingsModalUtils";
import {
  handleDefaultConfigurationUpdate,
  handleGradeConfigurationUpdate,
  isConfigurationEquivalent,
} from "../modules/updateHandlers";
import { buildNavigationOptions } from "../modules/navigationHelpers";
import { createGradeMapCache } from "../modules/gradeMapCache";
import { useGradingPeriodsMappingState } from "./useGradingPeriodsMappingState";

const buildEffectiveGradingPeriods = ({ gradeMapId, gradingPeriodDrafts }) => {
  if (!gradeMapId) return undefined;
  const draft = _.get(gradingPeriodDrafts, gradeMapId);
  if (!draft) return undefined;
  return mapDraftPeriodsToBackendShape(draft);
};

export const useDisplaySettingsModalData = ({
  academicRecordsQueryRef,
  currentStudent,
  overriddenTitleClassName,
  overriddenDotClassName,
}) => {
  const { t } = useTranslation(["common", "transcript"]);
  const dispatch = useDispatch();
  const { data: academicRecordsData } = useReadQuery(academicRecordsQueryRef);
  const { closeModal, modalInfo } = useAcademicRecordsModal();
  const metadataPath = _.get(modalInfo, "metadata.path", null);
  const [updates, setUpdates] = useState({});
  const [draftListItemsByKey, setDraftListItemsByKey] = useState({});
  const [isApplyingConfiguration, setIsApplyingConfiguration] = useState(false);

  const { data, networkStatus, refetch: refetchOrgGradeScales } = useQuery(
    getOrgTranscriptEntitiesQuery,
    {
      variables: { id: getOrganizationId() },
      fetchPolicy: "cache-and-network",
    }
  );
  const isLoading = _.isEmpty(data) && _.includes([1, 2], networkStatus);
  const shouldShowModalLoader = isApplyingConfiguration;

  // Create cached grade map lookup for O(1) performance
  const gradeMapCache = useMemo(
    () => createGradeMapCache(academicRecordsData),
    [academicRecordsData]
  );

  const {
    gradingPeriodDrafts,
    getCurrentPeriods,
    handlePeriodSelectionChange,
    handlePeriodToggle,
    handlePeriodReorder,
    buildAllPayloads,
    resetGrade,
    removeDeletedPeriodsFromDrafts,
  } = useGradingPeriodsMappingState({ gradeMapCache });

  const getBackendIsOverridden = useCallback(
    gradeMapId => gradeMapCache.isGradeOverridden(gradeMapId),
    [gradeMapCache]
  );

  // Declared up front because getLocalGradeDisplayState (below) depends
  // on them to build the per-grade inherited default.
  const orgGradeScalesData = useMemo(
    () => _.get(data, "node.transcriptGradeScales", []),
    [data]
  );
  const curriculumProgramsData = useMemo(
    () => _.get(data, "node.curriculumPrograms", []),
    [data]
  );

  // Stabilize the DEFAULT draft configuration reference so that
  // baselineByGradeId only recomputes when the default config actually
  // changes — not on every grade-level edit that spreads a new `updates`.
  const defaultDraftConfiguration = useMemo(
    () => _.get(updates, "DEFAULT.configuration", null),
    [updates]
  );

  // Pre-compute the inherited-default baseline for every grade once,
  // so getLocalGradeDisplayState can do a cheap lookup instead of
  // calling getResetDraftConfiguration (deep clones + filtering) per grade.
  const baselineByGradeId = useMemo(() => {
    const grades = _.get(
      academicRecordsData,
      "node.transcriptSummary.grades",
      []
    );
    const result = {};
    _.forEach(grades, gradeMap => {
      const gradeMapId = _.get(gradeMap, "id");
      const draftPeriods = _.get(gradingPeriodDrafts, gradeMapId, null);
      const backendPeriods = _.get(gradeMap, "gradingPeriods", []);
      const baselinePeriods = draftPeriods
        ? mapDraftPeriodsToBackendShape(draftPeriods)
        : backendPeriods;
      result[gradeMapId] = getResetDraftConfiguration({
        academicRecordsData,
        selectedGradeId: gradeMapId,
        defaultDraftConfiguration,
        orgGradeScalesData,
        curriculumProgramsData,
        effectivePeriods: baselinePeriods,
      });
    });
    return result;
  }, [
    academicRecordsData,
    curriculumProgramsData,
    defaultDraftConfiguration,
    gradingPeriodDrafts,
    orgGradeScalesData,
  ]);

  // Dot rule (per Display Settings invariants):
  //   dot(g) = ON  iff
  //     any GP in the effective mapping has isEnabled === false, OR
  //     effectiveGradeConfig ≉ grade-level inherited default
  //
  // The baseline is always the grade-level inherited default (rebuilt for
  // the current GP selection). For non-overridden backends it's effectively
  // the same as the backend config; for overridden backends it's the
  // "clean" state a revert should match; for a newly-added grade (no
  // backend config yet) it's the only meaningful comparison target; and
  // for newly-added GPs it's the only place to get sensible defaults for
  // the new period (so user customizations of that period register as
  // drift rather than being silently dropped).
  const getLocalGradeDisplayState = useCallback(
    gradeMapId => {
      const gradeUpdate = _.get(updates, gradeMapId, {});
      const isConfigDirty = _.get(gradeUpdate, "isDirty", false);
      const isResetToDefault = _.get(gradeUpdate, "isResetToDefault", false);

      const gradeMap = gradeMapCache.getGradeMap(gradeMapId);
      const backendConfiguration = _.get(gradeMap, "configuration", null);
      const effectiveGradeConfig =
        _.get(gradeUpdate, "configuration", null) || backendConfiguration;

      // Effective GP list: draft if user has touched GPs for this grade,
      // else the backend mapping. Dot flips on if any period is disabled.
      const draftPeriods = _.get(gradingPeriodDrafts, gradeMapId, null);
      const backendPeriodsForBaseline = _.get(gradeMap, "gradingPeriods", []);
      const effectivePeriods =
        draftPeriods ||
        _.map(backendPeriodsForBaseline, gp => ({
          gradingPeriodId: String(_.get(gp, "gradingPeriod.id")),
          isEnabled: _.get(gp, "isEnabled", true),
        }));
      const hasAnyDisabledGradingPeriod = _.some(
        effectivePeriods,
        p => p.isEnabled === false
      );

      const baseline = _.get(baselineByGradeId, gradeMapId, null);

      const configDrift =
        !_.isEmpty(baseline) &&
        !_.isEmpty(effectiveGradeConfig) &&
        !isConfigurationEquivalent(effectiveGradeConfig, baseline);

      const isOverridden = hasAnyDisabledGradingPeriod || configDrift;

      return {
        isDirty: isConfigDirty || hasAnyDisabledGradingPeriod,
        isResetToDefault,
        isOverridden,
      };
    },
    [baselineByGradeId, gradeMapCache, gradingPeriodDrafts, updates]
  );

  const gradeWiseOptions = useMemo(() => {
    return _.map(
      getGradeWiseOptions({
        academicRecordsData,
      }),
      option => {
        const gradeMapId = _.replace(
          _.get(option, "key", ""),
          GRADE_OPTION_KEY_PREFIX,
          ""
        );
        const { isOverridden } = getLocalGradeDisplayState(gradeMapId);

        return {
          ...option,
          isOverridden,
        };
      }
    );
  }, [academicRecordsData, getLocalGradeDisplayState]);

  const navOptions = useMemo(
    () =>
      buildNavigationOptions({
        sectionNodeKey: SECTION_NODE_KEY,
        gradeWiseNodeKey: GRADE_WISE_NODE_KEY,
        defaultColumnsRowsKey: DEFAULT_COLUMNS_ROWS_KEY,
        acrossYearAggregatesKey: ACROSS_YEAR_AGGREGATES_KEY,
        gradingPeriodsSettingsKey: GRADING_PERIODS_SETTINGS_KEY,
        gradeWiseOptions,
        overriddenTitleClassName,
        overriddenDotClassName,
        t,
      }),
    [gradeWiseOptions, overriddenDotClassName, overriddenTitleClassName, t]
  );

  const selectableKeys = useMemo(
    () =>
      getSelectableKeys({
        gradeWiseOptions,
      }),
    [gradeWiseOptions]
  );

  const initialSelectedTab = useMemo(() => {
    return getInitialSelectedTab({
      metadataSelectedTab: metadataPath,
    });
  }, [metadataPath]);

  const [selectedTab, setSelectedTab] = useState(initialSelectedTab);

  const selectedGradeId = useMemo(() => {
    return getSelectedGradeId({
      selectedTab,
    });
  }, [selectedTab]);

  const selectedDraftKey = useMemo(
    () => getSelectedDraftKey({ selectedGradeId }),
    [selectedGradeId]
  );

  const selectedListItemsKey = `${selectedDraftKey}__${selectedTab}`;

  const selectedTabConfig = useMemo(
    () => _.find(navOptions, { key: selectedTab }),
    [navOptions, selectedTab]
  );
  const {
    isOverridden: isSelectedGradeOverridden,
    isDirty: isSelectedDraftDirty,
    isResetToDefault: isSelectedGradeResetToDefault,
  } = useMemo(
    () =>
      selectedGradeId
        ? getLocalGradeDisplayState(selectedGradeId)
        : {
            isOverridden: false,
            isDirty: false,
            isResetToDefault: false,
          },
    [getLocalGradeDisplayState, selectedGradeId]
  );

  const handleSideNavigationClick = useCallback(
    key => {
      if (key && selectableKeys.has(key)) {
        setSelectedTab(key);
      }
    },
    [selectableKeys]
  );

  const heading = useMemo(() => {
    if (selectedTab === GRADING_PERIODS_SETTINGS_KEY) {
      return t("common:grading_period_plural");
    }
    if (selectedTab === DEFAULT_COLUMNS_ROWS_KEY) {
      const headingKey = _.get(
        config,
        "defaultSettingsContent.defaultTab.headingKey",
        ""
      );
      return t(headingKey);
    }
    return selectedTabConfig?.title || "";
  }, [selectedTab, selectedTabConfig, t]);

  const paragraph = useMemo(() => {
    const contentMap = {
      [DEFAULT_COLUMNS_ROWS_KEY]: _.get(
        config,
        "defaultSettingsContent.defaultTab.paragraphKey",
        ""
      ),
      [ACROSS_YEAR_AGGREGATES_KEY]:
        "transcript:display_settings_across_year_aggregates_paragraph",
      [GRADING_PERIODS_SETTINGS_KEY]:
        "transcript:display_settings_grading_periods_paragraph",
    };

    const key =
      contentMap[selectedTab] ||
      _.get(config, "defaultSettingsContent.gradeTab.paragraphKey", "");

    return t(key);
  }, [selectedTab, t]);

  const handleUpdatesChange = useCallback(
    update => {
      const key = _.get(update, "key");
      if (!key) return;

      setUpdates(prev => {
        const prevUpdate = _.get(prev, key, {});

        // Handle default configuration separately
        const nextUpdate =
          key === "DEFAULT"
            ? handleDefaultConfigurationUpdate({
                update,
                academicRecordsData,
              })
            : handleGradeConfigurationUpdate({
                update,
                prevUpdate,
                academicRecordsData,
                getBackendIsOverridden,
                getResetDraftConfiguration,
                defaultDraftConfiguration: _.get(
                  prev,
                  "DEFAULT.configuration",
                  null
                ),
                orgGradeScalesData,
                curriculumProgramsData,
                effectiveGradingPeriods: buildEffectiveGradingPeriods({
                  gradeMapId: _.get(update, "selectedGradeId"),
                  gradingPeriodDrafts,
                }),
              });

        // Prevent unnecessary state updates
        if (_.isEqual(prevUpdate, nextUpdate)) {
          return prev;
        }

        return {
          ...prev,
          [key]: nextUpdate,
        };
      });
    },
    [
      academicRecordsData,
      curriculumProgramsData,
      getBackendIsOverridden,
      gradingPeriodDrafts,
      orgGradeScalesData,
    ]
  );

  const handleListItemsChange = useCallback(
    ({ key, listItems }) => {
      if (!key) return;

      setDraftListItemsByKey(prev => {
        const prevListItems = _.get(prev, selectedListItemsKey);
        if (_.isEqual(prevListItems, listItems)) {
          return prev;
        }

        return {
          ...prev,
          [selectedListItemsKey]: listItems,
        };
      });
    },
    [selectedListItemsKey]
  );

  const handleResetToDefault = useCallback(
    ({ onCompleted } = {}) => {
      if (!selectedGradeId) {
        return;
      }

      const resetConfiguration = getResetDraftConfiguration({
        academicRecordsData,
        selectedGradeId,
        defaultDraftConfiguration: _.get(
          updates,
          "DEFAULT.configuration",
          null
        ),
        orgGradeScalesData,
        curriculumProgramsData,
      });
      if (_.isEmpty(resetConfiguration)) {
        return;
      }

      setUpdates(prev => {
        return {
          ...prev,
          [selectedDraftKey]: {
            key: selectedDraftKey,
            selectedGradeId,
            configuration: resetConfiguration,
            resetConfigurationSnapshot: resetConfiguration,
            isDirty: false,
            isResetToDefault: true,
          },
        };
      });
      setDraftListItemsByKey(prev => _.omit(prev, selectedListItemsKey));
      resetGrade(selectedGradeId);
      if (onCompleted) {
        onCompleted();
      }
    },
    [
      academicRecordsData,
      selectedDraftKey,
      selectedListItemsKey,
      selectedGradeId,
      updates,
      orgGradeScalesData,
      curriculumProgramsData,
      resetGrade,
    ]
  );

  const draftConfiguration = useMemo(
    () => getDraftConfiguration({ updates, selectedGradeId }),
    [selectedGradeId, updates]
  );

  const defaultGradeScalesConfig = useMemo(() => {
    const defaultUpdate = _.get(updates, "DEFAULT", {});
    const defaultConfig =
      _.get(defaultUpdate, "configuration") ||
      _.get(academicRecordsData, "node.transcriptSummary.configuration");
    return _.get(defaultConfig, "gradeScales", {});
  }, [updates, academicRecordsData]);

  const [gpErrorGradeMapIds, setGpErrorGradeMapIds] = useState([]);

  const validateGradingPeriods = useCallback(async () => {
    const grades = _.get(
      academicRecordsData,
      "node.transcriptSummary.grades",
      []
    );
    const emptyGradeMapIds = [];
    _.forEach(grades, gradeMap => {
      const gradeMapId = _.get(gradeMap, "id");
      const periods = getCurrentPeriods(gradeMapId) || [];
      if (_.isEmpty(periods)) {
        emptyGradeMapIds.push(gradeMapId);
      }
    });

    if (!_.isEmpty(emptyGradeMapIds)) {
      setGpErrorGradeMapIds(emptyGradeMapIds);
      dispatch(
        setToastMsg({
          type: "error",
          msg: "transcript:fill_required_fields_error",
        })
      );
      setSelectedTab(GRADING_PERIODS_SETTINGS_KEY);
      return false;
    }

    setGpErrorGradeMapIds([]);
    return true;
  }, [academicRecordsData, getCurrentPeriods, dispatch]);

  const handleApplyForStudent = useCallback(async () => {
    const isValid = await validateGradingPeriods();
    if (!isValid) return;

    const sourceStudentId = _.get(academicRecordsData, "node.id");
    if (!sourceStudentId) {
      return;
    }

    const configurationPayload = getConfigurationPayload({
      academicRecordsData,
      updates,
      gradingPeriodDrafts,
      orgGradeScalesData,
      curriculumProgramsData,
    });

    const { added, removed } = buildAllPayloads();
    const hasConfigChanges = !_.isEmpty(configurationPayload);
    const hasGpChanges = !_.isEmpty(added) || !_.isEmpty(removed);

    if (!hasConfigChanges && !hasGpChanges) {
      return;
    }

    setIsApplyingConfiguration(true);

    if (hasGpChanges) {
      const { isSuccess } = await dispatch(
        updateStudentGradeGradingPeriodMappingHandler({
          input: { added, removed },
        })
      );
      if (!isSuccess) {
        setIsApplyingConfiguration(false);
        return;
      }
    }

    if (!hasConfigChanges) {
      setIsApplyingConfiguration(false);
      closeModal();
      return;
    }

    dispatch(
      updateTranscriptConfigurationHandler({
        input: [{ studentId: sourceStudentId, configurationPayload }],
        studentId: sourceStudentId,
        organizationId: getOrganizationId(),
        onCompleted: closeModal,
        onFinally: () => setIsApplyingConfiguration(false),
      })
    );
  }, [
    academicRecordsData,
    buildAllPayloads,
    closeModal,
    curriculumProgramsData,
    dispatch,
    gradingPeriodDrafts,
    orgGradeScalesData,
    updates,
  ]);

  const handleApplyForMultipleStudents = useCallback(
    async (selectedStudentIds, onComplete) => {
      if (!selectedStudentIds?.length) {
        return;
      }

      const sourceStudentId = _.get(academicRecordsData, "node.id");
      const configurationPayload = getBulkConfigurationPayload({
        academicRecordsData,
        updates,
      });

      const targetStudentIds = _.filter(
        selectedStudentIds,
        id => id !== sourceStudentId
      );

      const hasConfigChanges = !_.isEmpty(configurationPayload);
      const { added, removed } = buildAllPayloads();
      const hasCurrentStudentGpChanges =
        !_.isEmpty(added) || !_.isEmpty(removed);

      if (hasCurrentStudentGpChanges) {
        const { isSuccess } = await dispatch(
          updateStudentGradeGradingPeriodMappingHandler({
            input: { added, removed },
          })
        );
        if (!isSuccess) {
          onComplete?.({ shouldClose: false });
          return;
        }
      }

      const { isSuccess: bulkGpSuccess } = await dispatch(
        bulkUpdateStudentGradeGradingPeriodMappingHandler({
          sourceStudentId,
          targetStudentIds,
        })
      );
      if (!bulkGpSuccess) {
        onComplete?.({ shouldClose: false });
        return;
      }

      if (!hasConfigChanges) {
        onComplete?.();
        return;
      }

      dispatch(
        bulkCopyTranscriptConfigurationHandler({
          sourceStudentId,
          targetStudentIds,
          configurationPayload,
          onFinally: onComplete,
        })
      );
    },
    [academicRecordsData, buildAllPayloads, dispatch, updates]
  );

  // Org-level grading periods for name resolution (covers newly added periods)
  const transcriptGradingPeriods = useMemo(
    () => _.get(data, "node.transcriptGradingPeriods", []),
    [data]
  );

  // Build name lookup from both backend grade periods AND org-level periods
  const gradingPeriodNameById = useMemo(() => {
    const nameMap = {};
    // First: org-level transcript grading periods
    _.forEach(transcriptGradingPeriods, gp => {
      const id = String(_.get(gp, "id", ""));
      if (id) {
        nameMap[id] = _.get(gp, "name", "");
      }
    });
    // Second: grade-level backend periods (may have different names, takes precedence)
    const grades = _.get(
      academicRecordsData,
      "node.transcriptSummary.grades",
      []
    );
    _.forEach(grades, gradeMap => {
      _.forEach(_.get(gradeMap, "gradingPeriods", []), gp => {
        const id = String(_.get(gp, "gradingPeriod.id", ""));
        if (id) {
          nameMap[id] = _.get(gp, "gradingPeriod.name", nameMap[id] || "");
        }
      });
    });
    return nameMap;
  }, [transcriptGradingPeriods, academicRecordsData]);

  // Cross-system: derive effective grading periods from draft state.
  // Filters to isEnabled: true only so the Columns UI hides disabled periods.
  // Shaped like backend data for compatibility with useTranscriptData/getTermWiseColumnSections.
  const effectiveGradingPeriods = useMemo(() => {
    if (!selectedGradeId) return [];
    const draft = _.get(gradingPeriodDrafts, selectedGradeId);

    if (!draft) {
      const gradeMap = gradeMapCache.getGradeMap(selectedGradeId);
      return _.sortBy(
        _.filter(_.get(gradeMap, "gradingPeriods", []), "isEnabled"),
        "displaySequence"
      );
    }

    const enabledPeriods = _.filter(draft, p => p.isEnabled);

    const result = _.map(enabledPeriods, p => ({
      gradingPeriod: {
        id: p.gradingPeriodId,
        name: _.get(
          gradingPeriodNameById,
          String(p.gradingPeriodId),
          p.gradingPeriodId
        ),
      },
      displaySequence: p.displaySequence,
    }));

    return result;
  }, [
    gradingPeriodDrafts,
    selectedGradeId,
    gradeMapCache,
    gradingPeriodNameById,
  ]);

  const handleSaveGradingPeriods = useCallback(
    ({ input, onCompleted, onFinally }) => {
      const deletedGpIds = _.map(input.removed, "id");
      dispatch(
        updateTranscriptGradingPeriodsHandler({
          input,
          onCompleted: () => {
            if (!_.isEmpty(deletedGpIds)) {
              removeDeletedPeriodsFromDrafts(deletedGpIds);
            }
            if (onCompleted) onCompleted();
          },
          onFinally,
        })
      );
    },
    [dispatch, removeDeletedPeriodsFromDrafts]
  );

  const gradingPeriodsSectionProps = useMemo(
    () => ({
      academicRecordsData,
      getCurrentPeriods,
      transcriptGradingPeriods,
      onPeriodSelectionChange: handlePeriodSelectionChange,
      onSaveGradingPeriods: handleSaveGradingPeriods,
      isLoading,
      errorGradeMapIds: gpErrorGradeMapIds,
      onClearErrors: () => setGpErrorGradeMapIds([]),
    }),
    [
      academicRecordsData,
      getCurrentPeriods,
      transcriptGradingPeriods,
      handlePeriodSelectionChange,
      handleSaveGradingPeriods,
      isLoading,
      gpErrorGradeMapIds,
    ]
  );

  const gradingPeriodsTabPeriods = useMemo(() => {
    if (!selectedGradeId) return [];
    const currentPeriods = getCurrentPeriods(selectedGradeId);
    return _.map(currentPeriods, p => ({
      gradingPeriodId: p.gradingPeriodId,
      name: _.get(
        gradingPeriodNameById,
        String(p.gradingPeriodId),
        p.gradingPeriodId
      ),
      isEnabled: p.isEnabled,
    }));
  }, [selectedGradeId, getCurrentPeriods, gradingPeriodNameById]);

  const defaultSettingsContentProps = useMemo(
    () => ({
      effectiveGradingPeriods,
      defaultGradeScalesConfig,
      gradingPeriodsTabProps: {
        periods: gradingPeriodsTabPeriods,
        gradeMapId: selectedGradeId,
        onToggle: handlePeriodToggle,
        onReorder: handlePeriodReorder,
      },
      academicRecordsData,
      orgGradeScalesData,
      curriculumProgramsData,
      selectedGradeId,
      onUpdatesChange: handleUpdatesChange,
      onRefetchOrgGradeScales: refetchOrgGradeScales,
      draftConfiguration,
      initialListItems: draftListItemsByKey[selectedListItemsKey],
      initialIsDirty: _.get(updates, [selectedDraftKey, "isDirty"], false),
      resetConfigurationSnapshot: _.get(
        updates,
        [selectedDraftKey, "resetConfigurationSnapshot"],
        null
      ),
      onListItemsChange: handleListItemsChange,
      isAcrossYearAggregatesMode: selectedTab === ACROSS_YEAR_AGGREGATES_KEY,
      isLoading,
    }),
    [
      academicRecordsData,
      curriculumProgramsData,
      defaultGradeScalesConfig,
      draftConfiguration,
      draftListItemsByKey,
      effectiveGradingPeriods,
      gradingPeriodsTabPeriods,
      handleListItemsChange,
      handlePeriodReorder,
      handlePeriodToggle,
      handleUpdatesChange,
      isLoading,
      orgGradeScalesData,
      refetchOrgGradeScales,
      selectedDraftKey,
      selectedListItemsKey,
      selectedGradeId,
      selectedTab,
      updates,
    ]
  );

  // Reset-to-Default button is enabled whenever the red dot is visible
  // for the currently-selected grade — i.e. the grade is in an overridden
  // state (config drift OR any disabled GP). Matches the dot semantics so
  // the user always has a "clear this override" affordance when a dot is
  // showing, and the button stays disabled when nothing is overridden.
  const isResetDisabled =
    isApplyingConfiguration || !selectedGradeId || !isSelectedGradeOverridden;

  const isGradingPeriodsSettingsMode =
    selectedTab === GRADING_PERIODS_SETTINGS_KEY;

  return {
    closeModal,
    defaultSettingsContentProps,
    gradingPeriodsSectionProps,
    isGradingPeriodsSettingsMode,
    handleApplyForStudent,
    handleApplyForMultipleStudents,
    validateGradingPeriods,
    handleResetToDefault,
    handleSideNavigationClick,
    heading,
    isResetDisabled,
    navOptions,
    paragraph,
    selectedDraftKey,
    selectedGradeId,
    selectedTab,
    shouldShowModalLoader,
    currentStudent,
    t,
  };
};
